import {
  applyClipToHtmlAudio,
  effectivePlaybackVolume,
  getClipById,
  getChaosClipByCardKey,
  resolveTriviaClipId,
  resolveUiClipId,
} from "./index";
import type { SoundClip, TriviaSoundEvent } from "./types";

let bgmEl: HTMLAudioElement | null = null;
/** Manifest `music` clip id currently wired to `bgmEl` (menu vs duel). */
let bgmActiveId: string | null = null;
let audioCtx: AudioContext | null = null;

function resumeAudioContext(): void {
  if (typeof window === "undefined") return;
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return;
    if (!audioCtx) audioCtx = new AC();
    void audioCtx.resume().catch(() => {
      /* noop */
    });
  } catch {
    /* noop */
  }
}

/**
 * Run from a tap/click (capture-phase listener is fine). Resumes Web Audio and
 * opens playback for this navigation without consuming the whole gesture on BGM.
 */
export function unlockGameAudio(): void {
  resumeAudioContext();
}

export function isGameAudioUnlocked(): boolean {
  return audioCtx?.state === "running";
}

function playClip(clip: SoundClip | undefined): void {
  if (typeof window === "undefined" || !clip) return;
  const el = new Audio();
  applyClipToHtmlAudio(el, clip);
  void el.play().catch(() => {
    /* autoplay / decode / 404 — ignore */
  });
}

export function playClipById(id: string): void {
  if (!id.trim()) return;
  playClip(getClipById(id));
}

export function playChaosClipForCardKey(cardKey: string): void {
  playClip(getChaosClipByCardKey(cardKey));
}

/** Button / control click — clip from `uiBindings.buttonClick` (default `button_click`). */
export function playUiTap(): void {
  const id = resolveUiClipId("buttonClick");
  if (id) playClipById(id);
}

/** "Plonk" — used when an item lands in a slot (doober reaches HUD). */
export function playUiPlace(): void {
  playClipById("place");
}

let lastDooberFlySoundPerf = -999;
/**
 * Reward / score chip / coin arcs toward the HUD — `doober_fly` in `sound-effects.json`.
 * Brief throttle keeps stacked coin waves from turning into machine-gun SFX.
 */
export function playDooberFly(): void {
  if (typeof window === "undefined") return;
  try {
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - lastDooberFlySoundPerf < 62) return;
    lastDooberFlySoundPerf = now;
    playClipById("doober_fly");
  } catch {
    /* noop */
  }
}

let lastDooberLandSoundPerf = -999;
/**
 * Doober settles into the HUD / counter — `doober_land` in `sound-effects.json`.
 * Throttled separately from `playDooberFly` so fly + land back-to-back both play.
 */
export function playDooberLand(): void {
  if (typeof window === "undefined") return;
  try {
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - lastDooberLandSoundPerf < 62) return;
    lastDooberLandSoundPerf = now;
    playClipById("doober_land");
  } catch {
    /* noop */
  }
}

/** Soft reject — used when a purchase / action fails (e.g. not enough coins). */
export function playUiReject(): void {
  playClipById("lose");
}

/** Big celebration — used when a purchase succeeds. */
export function playUiCelebrate(): void {
  playClipById("win");
}

/** Match results — user claims 2× coins/XP (`reward_2x_claim` in `sound-effects.json`). */
export function playReward2xClaim(): void {
  playClipById("reward_2x_claim");
}

/** Daily rewards / streak modal — successful `/daily-reward/claim` (`daily_reward_claim`). */
export function playDailyRewardClaim(): void {
  playClipById("daily_reward_claim");
}

let lastMatchOutcomeSoundPerf = -999;
/**
 * Duel finished — Victory / Defeat / Draw takeover (`sound-effects.json` → `match` clips).
 * Brief throttle avoids duplicate cues under React Strict Mode dev double-mount.
 */
export function playMatchOutcomeSound(
  outcome: "win" | "loss" | "draw",
): void {
  if (typeof window === "undefined") return;
  try {
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - lastMatchOutcomeSoundPerf < 220) return;
    lastMatchOutcomeSoundPerf = now;
    if (outcome === "win") playClipById("win");
    else if (outcome === "loss") playClipById("lose");
    else playClipById("place");
  } catch {
    /* noop */
  }
}

/** Trivia wheel starts rotating — clip id from `sound-effects.json` → `triviaBindings.spinStart`. */
export function playTriviaSpin(): void {
  const id = resolveTriviaClipId("spinStart");
  if (id) playClipById(id);
}

/** Wheel eased onto the winning wedge — `triviaBindings.spinLand`. */
export function playTriviaSpinLand(): void {
  const id = resolveTriviaClipId("spinLand");
  if (id) playClipById(id);
}

/** Graded correct — `triviaBindings.answerCorrect`. */
export function playTriviaCorrect(): void {
  const id = resolveTriviaClipId("answerCorrect");
  if (id) playClipById(id);
}

/** Graded wrong — `triviaBindings.answerWrong`. */
export function playTriviaWrong(): void {
  const id = resolveTriviaClipId("answerWrong");
  if (id) playClipById(id);
}

/** Crown wedge — `triviaBindings.crownLand`. */
export function playTriviaCrown(): void {
  const id = resolveTriviaClipId("crownLand");
  if (id) playClipById(id);
}

/** Duel powerup kinds wired in `triviaBindings` (`powerUpFifty` / `powerUpTime` / `powerUpSkip`). */
export type DuelPowerUpSoundKind = "fifty" | "time" | "skip";

function triviaEventForPowerUp(kind: DuelPowerUpSoundKind): TriviaSoundEvent {
  if (kind === "fifty") return "powerUpFifty";
  if (kind === "time") return "powerUpTime";
  return "powerUpSkip";
}

/** One clip per powerup use — which clip is chosen from `kind`. */
export function playPowerUp(kind: DuelPowerUpSoundKind): void {
  const id = resolveTriviaClipId(triviaEventForPowerUp(kind));
  if (id) playClipById(id);
}

const BGM_MENU_ID = "bgm_menu";
const BGM_DUEL_ID = "bgm_loop";

function ambientBgmIdForPath(pathname: string): string {
  if (pathname === "/play") return BGM_DUEL_ID;
  return BGM_MENU_ID;
}

/** Re-apply manifest × user prefs to the looping BGM element (mute / music volume). */
export function syncBgmPlaybackVolume(): void {
  if (typeof window === "undefined") return;
  if (!bgmEl || !bgmActiveId) return;
  const clip = getClipById(bgmActiveId);
  if (!clip) return;
  bgmEl.volume = effectivePlaybackVolume(clip);
}

/**
 * Route-aware ambient loop: `bgm_menu` off duel routes, `bgm_loop` under `/play`.
 * Safe after audio unlock; swaps tracks only when menu vs duel changes.
 * Null/empty pathname is ignored so client navigations do not stop and restart the same track.
 */
export function applyAmbientBgmForPath(pathname: string | null): void {
  if (typeof window === "undefined") return;
  // Empty pathname can occur briefly during client nav; do not stop BGM or the next apply restarts the track.
  if (pathname == null || pathname === "") return;

  const id = ambientBgmIdForPath(pathname);
  const clip = getClipById(id);
  if (!clip) {
    stopBgm();
    return;
  }
  if (!bgmEl) bgmEl = new Audio();

  const switching = bgmActiveId !== id;
  bgmActiveId = id;

  if (switching) {
    applyClipToHtmlAudio(bgmEl, clip);
    void bgmEl.play().catch(() => {});
    return;
  }

  syncBgmPlaybackVolume();
  if (!bgmEl.paused) return;
  void bgmEl.play().catch(() => {});
}

export function stopBgm(): void {
  if (!bgmEl) return;
  bgmEl.pause();
  bgmEl.currentTime = 0;
  bgmActiveId = null;
}
